import * as React from 'react';

import { ReportScopeBanner } from 'pages/reports/components/ReportScopeBanner';
import { EarlyAccessChip } from 'pages/reports/components/EarlyAccessChip';
import { GenerateReport } from 'components/GenerateReport/GenerateReport';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { isInsightReport } from 'pages/reports/utils/insightReports';
import { HelpSidebarTrigger } from 'components/help_sidebar';
import type { ReportPermissions } from 'pages/reports/types';
import { CategoryReport, Report } from 'apis/ReportApi';
import { helpContent } from 'pages/reports/help';
import { FormattedMessage } from 'react-intl';
import { isEmbedded } from 'utils/other';
import { Button } from 'react-bootstrap';

interface ReportViewerProps {
    cultureId: number;
    currentlySelectedReport: Report;
    highestRole: { Id: number; Name: string };
    listOfReports: CategoryReport[];
    onBack: () => void;
    onChangeScope: () => void;
    onPrint: () => void;

    /** Forwarded from `<GenerateReport>` — the parent uses this to capture RelatedReports and student completion date. */
    onReportLoaded: (report: Report) => void;

    /** Called when a related-report tab is clicked. */
    onSelectRelatedReport: (report: Report) => void;
    permissions: ReportPermissions;
    relatedReports: Report[];
    scopeObject: { Id: number; Name: string };
    selectedPartner: { Id: number; Name: string };
    studentCompletionDate: string | null;

    /** Optional override for the title (used when a custom report is opened with a TestName param). */
    testName: string | null;
}

/**
 * Viewer half of the Reports page. Renders the Back link, the GMetrixTabs strip
 * (gated on `relatedReports.length > 0`), the report header with optional
 * Print button for student-scoped reports, and the `<GenerateReport>` element.
 *
 * Purely presentational — all state and handlers live in the parent.
 */
export const ReportViewer = (props: ReportViewerProps) => {

    const {
        cultureId,
        currentlySelectedReport,
        highestRole,
        listOfReports,
        onBack,
        onChangeScope,
        onPrint,
        onReportLoaded,
        onSelectRelatedReport,
        permissions,
        relatedReports,
        scopeObject,
        selectedPartner,
        studentCompletionDate,
        testName
    } = props;

    // In an embedded (modal) view the parent report already set the scope/filters,
    // so hide the chrome (back, tabs, header, filters) and lock the filters.
    const embedded = isEmbedded();

    const isStudentScoped = currentlySelectedReport.ScopeId === 8 || currentlySelectedReport.ScopeId === 10;

    return (
        <>
            {!permissions.IsStudent && !embedded && (
                <div
                    className="d-flex justify-content-start"
                    onClick={onBack}
                    style={{ cursor: 'pointer', marginTop: '1rem' }}
                >
                    <i className="fa fa-arrow-left fa-lg me-2"></i>
                    <h4 style={{ lineHeight: '0px' }}>
                        <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                    </h4>
                </div>
            )}

            {relatedReports.length > 0 && !embedded && (() => {

                const sortedRelated = [...relatedReports].sort((a: any, b: any) => a.RelatedReportId - b.RelatedReportId);

                // Derive the highlighted tab from the currently-selected report so it always
                // matches whichever related report is being viewed, regardless of how it was chosen.
                const activeTabIndex = Math.max(0, sortedRelated.findIndex((r: any) => r.RelatedReportId === currentlySelectedReport.Id));

                return (
                    <GMetrixTabs
                        onSelect={i => {

                            const related: any = sortedRelated[i];
                            const selectedReportId = related?.RelatedReportId;
                            if (!selectedReportId) return;

                            // Prefer the canonical entry from listOfReports (it carries the full
                            // Report shape including filters), but fall back to the related-report
                            // stub so sub-reports that aren't in the main catalog still work.
                            const canonical = listOfReports.flatMap(category => category.Reports).find(r => r.Id === selectedReportId);
                            const report: Report = canonical ?? ({
                                ...related,
                                Id: selectedReportId,
                                ScopeId: related.ScopeId ?? currentlySelectedReport.ScopeId
                            } as Report);
                            onSelectRelatedReport(report);

                        }}
                        className="report-tab-strip mt-4"
                        selected={activeTabIndex}
                    >
                        {sortedRelated.map((report: any, index) => (
                            <GMetrixTabsTab
                                title={(
                                    <div className="tabs__tab-title">
                                        {report.Name}
                                    </div>
                                )}
                                key={report.RelatedReportId ?? index}
                                tabPaneClassName="d-none"
                            />
                        ))}
                    </GMetrixTabs>
                );

            })()}

            {!permissions.IsStudent && !embedded && (
                <>
                    <h3 className="d-flex justify-content-between align-items-center w-100 text-strong pt-4">
                        <span className="d-flex align-items-center gap-2">
                            {testName ?? currentlySelectedReport.Name}
                            {isInsightReport(currentlySelectedReport.Id) && (
                                <EarlyAccessChip id={`early-access-report-${currentlySelectedReport.Id}`} />
                            )}
                        </span>
                        <HelpSidebarTrigger content={helpContent} />
                    </h3>
                    <ReportScopeBanner
                        highestRoleId={highestRole.Id}
                        onChangeScope={onChangeScope}
                        scopeId={currentlySelectedReport.ScopeId}
                        scopeObject={scopeObject}
                    />

                    {isStudentScoped && (
                        <div className="align-items-center d-flex justify-content-between">
                            <div className="mb-3">
                                {studentCompletionDate && (
                                    <div className="text-muted">
                                        <strong>
                                            <FormattedMessage defaultMessage="Completion Date:" id="completion_date.52bf61ad" />
                                        </strong>
                                        {' '}
                                        {studentCompletionDate}
                                    </div>
                                )}
                            </div>
                            <div>
                                <Button
                                    className="fw-bold float-end d-print-none"
                                    onClick={onPrint}
                                    size="sm"
                                    variant="outline-primary"
                                >
                                    <i aria-hidden="true" className="fa-regular fa-print me-2"></i>
                                    {<FormattedMessage defaultMessage="Print" id="print.1fd3b13a" />}
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
            <hr />

            <GenerateReport
                urlSync
                cultureId={cultureId}
                highestRole={highestRole}
                key={`${currentlySelectedReport.Id}_${scopeObject.Id}`}
                onReportLoaded={onReportLoaded}
                reportId={currentlySelectedReport.Id}
                scopeId={currentlySelectedReport.ScopeId}
                scopeObjectId={scopeObject.Id}
                showNoFilters={embedded}
            />
        </>
    );

};
